import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Header from './components/Header';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth } from './hooks/useAuth';
import Dashboard from './pages/Dashboard';
import TasksPage from './pages/TasksPage';
import BudgetPage from './pages/BudgetPage';
import PhasesPage from './pages/PhasesPage';
import ContactsPage from './pages/ContactsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AppointmentsPage from './pages/AppointmentsPage';
import InvoicesPage from './pages/InvoicesPage';
import PhaseDetail from './pages/PhaseDetail';
import DocumentsPage from './pages/DocumentsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const API_URL = '/api';

function AppContent() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [tasks, setTasks] = useState([]);
  const [phases, setPhases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);

  // Charger toutes les données
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [tasksRes, phasesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/tasks`, { headers }),
        axios.get(`${API_URL}/phases`, { headers }),
        axios.get(`${API_URL}/stats`, { headers })
      ]);
      
      setTasks(tasksRes.data);
      setPhases(phasesRes.data);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement:', err);
      setError('Erreur de connexion avec le serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (taskData) => {
    try {
      const response = await axios.post(`${API_URL}/tasks`, taskData);
      setTasks([...tasks, response.data]);
      // Recharger les stats après l'ajout d'une tâche
      loadData();
      return response.data;
    } catch (err) {
      console.error('Erreur:', err);
      throw err;
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await axios.put(`${API_URL}/tasks/${taskId}`, updates);
      setTasks(tasks.map(t => t.id === taskId ? response.data : t));
      // Recharger les stats après la modification d'une tâche
      loadData();
      return response.data;
    } catch (err) {
      console.error('Erreur:', err);
      throw err;
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`);
      setTasks(tasks.filter(t => t.id !== taskId));
      // Recharger les stats après la suppression d'une tâche
      loadData();
    } catch (err) {
      console.error('Erreur:', err);
      throw err;
    }
  };

  const openPhase = (phaseId) => {
    setSelectedPhaseId(phaseId);
    setCurrentPage('phase-detail');
  };

  if (!user) return null;

  return (
    <div className="App">
      <Header currentPage={currentPage} onPageChange={setCurrentPage} />
      
      <main className="main-content">
        {loading && <div className="loading">⏳ Chargement...</div>}
        {error && <div className="error">❌ {error}</div>}
        
        {!loading && !error && (
          <>
            {currentPage === 'dashboard' && <Dashboard stats={stats} tasks={tasks} phases={phases} />}
            {currentPage === 'tasks' && <TasksPage tasks={tasks} phases={phases} onAddTask={handleAddTask} onUpdateTask={handleUpdateTask} onDeleteTask={handleDeleteTask} />}
            {currentPage === 'budget' && <BudgetPage tasks={tasks} onReload={loadData} />}
            {currentPage === 'phases' && <PhasesPage phases={phases} onReload={loadData} onOpenPhase={openPhase} />}
            {currentPage === 'phase-detail' && selectedPhaseId && (
              <PhaseDetail phaseId={selectedPhaseId} phases={phases} tasks={tasks} onAddTask={handleAddTask} onUpdateTask={handleUpdateTask} onDeleteTask={handleDeleteTask} onBack={() => setCurrentPage('phases')} />
            )}
            {currentPage === 'invoices' && <InvoicesPage />}
            {currentPage === 'documents' && <DocumentsPage phases={phases} tasks={tasks} onReload={loadData} />}
            {currentPage === 'appointments' && <AppointmentsPage />}
            {currentPage === 'contacts' && <ContactsPage apiUrl={API_URL} />}
            {currentPage === 'admin' && <AdminUsersPage />}
          </>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <AppContent />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;
